import {
  type APIMessage,
  avatarUrl,
  ChannelType,
  notificationFor,
  parseMarkdown,
  selectDisplayName,
  THREAD_CHANNEL_TYPES,
  toPlainText,
} from '@alternativamais/chat-core';
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { lerPreferencia } from '../armazenamento/local';
import { ARQUIVOS } from '../config';
import { abrirCamada } from './camadas';
import { useCliente } from './contexto';
import { lerPreferencias } from './preferencias';
import { tocarSom } from './sons';
import { ponteDesktop } from '../desktop/ponte';

/** Quando a pessoa disse "Agora não" no pedido (A18). */
export const PEDIDO_RECUSADO = 'notificacoes-recusadas-em';
/** Pede de novo depois de uma semana, como o Discord faz com o banner. */
const PEDIR_DE_NOVO_MS = 7 * 24 * 3600_000;

const suportado = () =>
  typeof window !== 'undefined' && 'Notification' in window;

/**
 * Notificações do navegador: cada mensagem nova passa pela regra do core
 * (`notificationFor`: nível do canal, silêncio, menções, "não perturbe");
 * não avisa da conversa aberta com a janela em foco. Clicar abre a
 * mensagem. Na primeira vez, explica e pede a permissão (A18).
 */
export function useNotificacoesDoNavegador() {
  const cliente = useCliente();
  const navegar = useNavigate();
  const local = useLocation();
  const aberta = useRef<string | null>(null);
  useEffect(() => {
    aberta.current = canalDaRota(local.pathname);
  }, [local.pathname]);

  useEffect(() => {
    // No app para computador o aviso é o do sistema: não há o que pedir.
    if (ponteDesktop()) return;
    if (!suportado() || Notification.permission !== 'default') return;
    const recusado = lerPreferencia<number>(PEDIDO_RECUSADO, 0);
    if (Date.now() - recusado < PEDIR_DE_NOVO_MS) return;
    const espera = window.setTimeout(
      () => abrirCamada({ tipo: 'pedir-notificacoes' }),
      4_000,
    );
    return () => window.clearTimeout(espera);
  }, []);

  useEffect(() => {
    return cliente.subscribe((tipo, dados) => {
      if (tipo !== 'MESSAGE_CREATE') return;
      const mensagem = dados as APIMessage;
      const estado = cliente.store.getState();
      if (!estado.me || mensagem.author.id === estado.me.id) return;
      if (document.hasFocus() && aberta.current === mensagem.channel_id) return;
      if (!notificationFor(estado, mensagem).notify) return;
      const canal = estado.channels[mensagem.channel_id];
      // O som (U3) toca mesmo sem a permissão de avisos do navegador.
      const meMenciona = mensagem.mentions.some(
        (pessoa) => pessoa.id === estado.me?.id,
      );
      tocarSom(
        meMenciona
          ? 'mencao'
          : !canal?.guild_id
            ? 'dm'
            : THREAD_CHANNEL_TYPES.has(canal.type)
              ? 'thread'
              : 'mensagem',
      );
      const ponte = ponteDesktop();
      if (
        !lerPreferencias().notificacoesNaTela ||
        (!ponte && (!suportado() || Notification.permission !== 'granted'))
      )
        return;
      const guildId = canal?.guild_id ?? null;
      const autor = selectDisplayName(estado, mensagem.author.id, guildId);
      let titulo = autor;
      if (canal?.type === ChannelType.GROUP_DM) {
        titulo = `${autor} (${canal.name || 'grupo'})`;
      } else if (guildId && canal) {
        const servidor = estado.guilds[guildId]?.name;
        titulo = `${autor} (#${canal.name}${servidor ? `, ${servidor}` : ''})`;
      }
      const texto = mensagem.content
        ? toPlainText(parseMarkdown(mensagem.content, { inline: true }), {
            user: (id) => selectDisplayName(estado, id, guildId),
            role: (id) =>
              (guildId && estado.guilds[guildId]?.roles[id]?.name) || 'cargo',
            channel: (id) => estado.channels[id]?.name ?? 'canal',
          })
        : mensagem.attachments.length
          ? 'Enviou um anexo.'
          : mensagem.sticker_items?.length
            ? 'Enviou uma figurinha.'
            : mensagem.poll
              ? `Enquete: ${mensagem.poll.question.text}`
              : 'Nova mensagem.';
      const rota = `/canais/${guildId ?? '@me'}/${mensagem.channel_id}/${mensagem.id}`;
      // App para computador: a notificação do sistema (E5), pela ponte.
      if (ponte) {
        ponte.notificar({
          titulo,
          corpo: texto.slice(0, 200),
          rota,
          tag: mensagem.channel_id,
        });
        return;
      }
      try {
        const aviso = new Notification(titulo, {
          body: texto.slice(0, 200),
          icon: avatarUrl(ARQUIVOS, mensagem.author) ?? undefined,
          tag: mensagem.channel_id,
        });
        aviso.onclick = () => {
          window.focus();
          navegar(rota);
          aviso.close();
        };
      } catch {
        // Navegador recusou (sem service worker no celular, por exemplo).
      }
    });
  }, [cliente, navegar]);
}

/** O canal aberto na rota (`/canais/:servidor/:canal`, ou a thread ao lado). */
function canalDaRota(caminho: string): string | null {
  const partes = caminho.split('/');
  if (partes[1] !== 'canais') return null;
  if (partes[4] === 'threads' && partes[5]) return partes[5];
  return partes[3] ?? null;
}
